import {
  NamespaceMapper,
  defaultBrandSettings,
  getBrandFromLocalStorage,
  getTFuncWithNamespace,
  getUseLocoTranslation,
} from '@systemeio/util'
import { TOptions } from 'i18next'
import { FormatXMLElementFn, PrimitiveType } from 'intl-messageformat'
import { TFunction, i18n, useTranslation } from 'next-i18next'
import { useCallback } from 'react'
import LocalizeNamespaces from 'shared/constants/localize-namespaces.mjs'
import { formUrl } from 'shared/utils/links/form-url'

export type LocalizeKeys = keyof typeof LocalizeNamespaces
export type LocalizeValues = typeof LocalizeNamespaces[LocalizeKeys]

export type TLocoType = ReturnType<typeof getTFuncWithNamespace>

const namespaceMapper: NamespaceMapper = (assetKey: string) => {
  if (assetKey === undefined) return undefined

  const basePart = assetKey.split('.')[0]

  switch (basePart) {
    case 'global':
    case 'core':
      return [LocalizeNamespaces.global]
    case 'header':
      return [LocalizeNamespaces.header]
    case 'confirmation_modal':
      return [LocalizeNamespaces.confirmation]
    case 'currency':
      return [LocalizeNamespaces.currency]
    case 'locale':
      return [LocalizeNamespaces.locale]
    case 'dashboard': {
      const namespaceFromKey = assetKey.split('.')[1]
      return [
        LocalizeNamespaces[namespaceFromKey as LocalizeKeys],
        ...Object.values(LocalizeNamespaces),
      ]
    }
    default:
      return undefined
  }
}

const defaultNamespaces = [
  LocalizeNamespaces.global,
  LocalizeNamespaces.header,
  LocalizeNamespaces.notifications,
  LocalizeNamespaces.locale,
]

const globalVariables = {
  brandName: (getBrandFromLocalStorage() ?? defaultBrandSettings).name,
  mainWebsiteUrl: formUrl('', false),
  dashboardUrl: formUrl(''),
}

export const tWithNamespace = (assetKey: string, options?: TOptions) => {
  if (!i18n?.t) return ''

  return getTFuncWithNamespace(i18n.t as unknown as TFunction, namespaceMapper)(assetKey, {
    ...globalVariables,
    ...options,
  })
}

const useLocoTranslationHook = getUseLocoTranslation({
  useTranslation,
  defaultNamespaces,
  namespaceMapper,
})

export const useLocoTranslation: typeof useLocoTranslationHook = () => {
  const { t: tTemp, tICU: tICUTemp, ...rest } = useLocoTranslationHook()

  const t = useCallback(
    (assetKey: string, options?: TOptions | undefined) => {
      return tTemp(assetKey, {
        ...globalVariables,
        ...options,
      })
    },
    [tTemp],
  )

  const tICU = useCallback(
    (
      assetKey: string,
      values: Record<string, PrimitiveType | FormatXMLElementFn<string>>,
      options?: TOptions,
    ) => {
      return tICUTemp(assetKey, values, {
        ...globalVariables,
        ...options,
      })
    },
    [tICUTemp],
  )

  return { t, tICU, ...rest }
}
